/**
 * AI Analysis Service
 *
 * This service handles AI-powered analysis of products, ingredients, suppliers, and packaging.
 * It provides methods to analyze data and apply fixes to the database.
 */

import { Api } from "./api";
import config from "../utils/config";

export type CollectionName = "product_list" | "ingredient_list" | "packaging" | "company";

export interface AnalyzeRequest {
	collection_name: CollectionName;
	limit: number;
	offset?: number;
	skip?: number;
}

export interface AnalyzeResponse {
	success: boolean;
	analysis_id?: string;
	data?: any;
	items_analyzed?: number;
	recommendations?: Array<{
		id: string;
		type: string;
		field: string;
		current_value?: any;
		suggested_value?: any;
		confidence?: number;
		reason?: string;
	}>;
	error?: string;
	// Full API response structure
	collection_name?: string;
	timestamp?: string;
	status?: string;
	summary?: {
		completeness_score: number;
		total_records: number;
		complete_records: number;
		incomplete_records: number;
		categories?: Array<{
			name: string;
			count: number;
			completeness_score: number;
			missing_fields: string[];
		}>;
		critical_issues?: string[];
	};
	compare?: {
		discrepancies?: Array<{
			field: string;
			internal_value: string;
			external_value: string;
			confidence_score: number;
			source: string;
			conflict_type: string;
		}>;
		confidence_scores?: Array<{
			field: string;
			score: number;
			reason: string;
			last_updated: string;
		}>;
	};
	push_suggestions?: {
		suggestions?: Array<{
			record_id: string;
			field: string;
			current_value: any;
			suggested_value: any;
			source: string;
			confidence: number;
			reason: string;
			action_required: string;
		}>;
		auto_fills?: Array<{
			record_id: string;
			field: string;
			value: any;
			source: string;
		}>;
		manual_review?: Array<{
			record_id: string;
			field: string;
			reason: string;
			priority: string;
		}>;
	};
	metadata?: {
		total_processing_time: number;
		records_processed: number;
		claude_model_used?: string;
	};
}

export interface FixPushRequest {
	analysis_id: string;
}

export interface FixPushResponse {
	success: boolean;
	message?: string;
	items_updated?: number;
	error?: string;
}

/**
 * AI Analysis Service
 * Extends the Api base class to leverage automatic authentication and error handling
 */
class AIAnalysisService extends Api {
	/**
	 * Analyze items in a collection
	 *
	 * @param request - The analysis request containing collection name and limit
	 * @returns Promise resolving to an AnalyzeResponse
	 */
	async analyze(request: AnalyzeRequest): Promise<AnalyzeResponse> {
		try {
			// Validate input
			if (!request.collection_name || !request.limit || request.limit <= 0) {
				return {
					success: false,
					error:
						"Invalid request parameters. Collection name and limit (greater than 0) are required."
				};
			}

			// Make API call using the http instance from Api base class
			const requestBody: any = {
				collection_name: request.collection_name,
				limit: request.limit
			};

			// Add offset or skip if provided
			if (request.offset !== undefined) {
				requestBody.offset = request.offset;
			} else if (request.skip !== undefined) {
				requestBody.skip = request.skip;
			}

			const response = await this.http.post(config.api.endpoints.aiAnalysis.analyze, requestBody);

			// Extract data from response - the API returns the full structure directly
			const responseData = response.data?.data || response.data;

			if (!responseData) {
				return {
					success: false,
					error: "Invalid response format from server"
				};
			}

			// Extract analysis_id
			const analysisId = responseData.analysis_id || responseData.id;

			// Extract items analyzed from summary or count
			const itemsAnalyzed =
				responseData.summary?.total_records ||
				responseData.items_analyzed ||
				responseData.count ||
				0;

			// Build recommendations from push_suggestions if available
			const suggestions = responseData.push_suggestions?.suggestions || [];
			const recommendations = suggestions.map((s: any) => ({
				id: s.record_id,
				type: s.action_required || "suggestion",
				field: s.field,
				current_value: s.current_value,
				suggested_value: s.suggested_value,
				confidence: s.confidence,
				reason: s.reason,
				item_id: s.record_id,
				source: s.source
			}));

			// Return full response structure
			return {
				success: true,
				analysis_id: analysisId,
				data: responseData,
				items_analyzed: itemsAnalyzed,
				recommendations: Array.isArray(recommendations) ? recommendations : [],
				// Include full API response structure
				collection_name: responseData.collection_name,
				timestamp: responseData.timestamp,
				status: responseData.status,
				summary: responseData.summary,
				compare: responseData.compare,
				push_suggestions: responseData.push_suggestions,
				metadata: responseData.metadata
			};
		} catch (error) {
			console.error("Error in analyze:", error);

			// Handle error response from API
			const axiosError = error as any;
			if (axiosError.response) {
				// Handle 503 Service Unavailable specifically
				if (axiosError.response.status === 503) {
					return {
						success: false,
						error:
							"Service temporarily unavailable. The AI analysis service is currently down or overloaded. Please try again in a few moments."
					};
				}

				const errorMessage =
					axiosError.response.data?.message ||
					axiosError.response.data?.error ||
					axiosError.response.data?.detail ||
					`Failed to analyze data (Status: ${axiosError.response.status})`;

				return {
					success: false,
					error: errorMessage
				};
			}

			// Handle network or other errors
			return {
				success: false,
				error:
					error instanceof Error
						? error.message
						: "Failed to analyze data. Please check your connection and try again."
			};
		}
	}

	/**
	 * Apply fixes from analysis to the database
	 *
	 * @param request - The fix push request containing analysis_id
	 * @returns Promise resolving to a FixPushResponse
	 */
	async fixPush(request: FixPushRequest): Promise<FixPushResponse> {
		try {
			// Validate input
			if (!request.analysis_id || typeof request.analysis_id !== "string") {
				return {
					success: false,
					error: "Analysis ID is required"
				};
			}

			// Make API call using the http instance from Api base class
			const response = await this.http.post(config.api.endpoints.aiAnalysis.fixPush, {
				analysis_id: request.analysis_id
			});

			// Extract data from response
			const responseData = response.data?.data || response.data;

			const itemsUpdated = responseData?.items_updated || responseData?.count || 0;
			const message =
				responseData?.message || response.data?.message || "Changes applied successfully";

			return {
				success: true,
				message: message,
				items_updated: itemsUpdated
			};
		} catch (error) {
			console.error("Error in fixPush:", error);

			// Handle error response from API
			const axiosError = error as any;
			if (axiosError.response) {
				// Handle 503 Service Unavailable specifically
				if (axiosError.response.status === 503) {
					return {
						success: false,
						error:
							"Service temporarily unavailable. The AI analysis service is currently down or overloaded. Please try again in a few moments."
					};
				}

				const errorMessage =
					axiosError.response.data?.message ||
					axiosError.response.data?.error ||
					axiosError.response.data?.detail ||
					`Failed to apply changes (Status: ${axiosError.response.status})`;

				return {
					success: false,
					error: errorMessage
				};
			}

			// Handle network or other errors
			return {
				success: false,
				error:
					error instanceof Error
						? error.message
						: "Failed to apply changes. Please check your connection and try again."
			};
		}
	}
}

// Create a singleton instance of the service
const aiAnalysisService = new AIAnalysisService();

/**
 * Analyze items API function
 *
 * @param request - The analysis request
 * @returns Promise resolving to an AnalyzeResponse
 */
export const analyzeAPI = async (request: AnalyzeRequest): Promise<AnalyzeResponse> => {
	return aiAnalysisService.analyze(request);
};

/**
 * Apply fixes API function
 *
 * @param request - The fix push request
 * @returns Promise resolving to a FixPushResponse
 */
export const fixPushAPI = async (request: FixPushRequest): Promise<FixPushResponse> => {
	return aiAnalysisService.fixPush(request);
};
